/**
 * External dependencies
 */
import gsap from 'gsap';

/**
 * 重ねたカードを1枚ずつめくる自動ループスライダー。
 * 後ろのカードは ±5deg で左右交互に傾け、奥ほど左右へはみ出して縮小する。
 */
const ROTATE = 5; // 後ろのカードの傾き(deg)。中心基点で左右交互に増やす。
const EXIT_X = 180; // めくれるカードが右へ抜ける距離(px)。
const EXIT_Y = -220; // めくれるカードが上へ抜ける距離(px)。
const EXIT_ROTATE = 12; // めくれるカードの傾き(deg)。
const MIN_DELAY = 800; // 切り替え間隔の下限(ms)。
const DEFAULT_DELAY = 2000;
const TRANSITION = 0.5; // 並び替えアニメーションの時間(s)。
const EXIT_DURATION = 0.45; // めくれアニメーションの時間(s)。

const BLOCK_SELECTOR = '.wp-block-best-anniversary-blocks-card-stack';
const STACK_SELECTOR = '.wp-block-best-anniversary-blocks-card-stack__stack';
const ITEM_SELECTOR = '.wp-block-best-anniversary-blocks-card-stack-item';

/**
 * スタック内の深さ(0=最前面)に応じた gsap プロパティを返す。
 * @param {number} depth - 最前面からの深さ
 * @param {number} total - カード総数
 * @return {Object} tween プロパティ
 */
function depthProps( depth, total ) {
	let rotation = 0;
	// 最前面(depth 0)と最背面(depth total-1)は 0deg。
	// 間のカードだけ中心基点で +5deg / -5deg を交互に付ける。
	if ( depth !== 0 && depth !== total - 1 ) {
		rotation = depth % 2 === 1 ? ROTATE : -ROTATE;
	}
	return {
		rotation,
		zIndex: total - depth,
		opacity: 1,
	};
}

function applyDepth( card, depth, total, animate ) {
	const props = depthProps( depth, total );
	if ( animate ) {
		gsap.to( card, { ...props, duration: TRANSITION, ease: 'power3.out' } );
	} else {
		gsap.set( card, props );
	}
}

/**
 * 1ブロックを初期化する。
 * @param {HTMLElement} block - カードスタックのルート要素
 */
function setupBlock( block ) {
	const stack = block.querySelector( STACK_SELECTOR );
	if ( ! stack ) {
		return;
	}

	const order = Array.from(
		stack.querySelectorAll( `:scope > ${ ITEM_SELECTOR }` )
	);
	const total = order.length;
	if ( total === 0 ) {
		return;
	}

	// 高さは先頭カード（通常配置）が決めるため、ここでは傾きの初期適用のみ。
	const layout = ( animate ) =>
		order.forEach( ( card, depth ) =>
			applyDepth( card, depth, total, animate )
		);

	layout( false );

	// 1枚しかないカードはループ不要。
	if ( total <= 1 ) {
		return;
	}

	const delay = Math.max(
		MIN_DELAY,
		parseInt( block.dataset.autoplayDelay, 10 ) || DEFAULT_DELAY
	);
	let timer;

	const runNext = () => {
		const front = order[ 0 ];

		const tl = gsap.timeline( { onComplete: scheduleNext } );

		// 最前面のカードを右上へめくって退場させる（位置0で開始）。
		// 退場中は最上位に固定し、後ろのカードが先に前面へ出ないようにする。
		tl.to(
			front,
			{
				x: EXIT_X,
				y: EXIT_Y,
				rotation: EXIT_ROTATE,
				opacity: 0,
				zIndex: total + 1,
				duration: EXIT_DURATION,
				ease: 'power2.in',
				overwrite: 'auto',
			},
			0
		);

		// 同時に、後ろのカードを1つ前の深さの傾きへ動かす（z-index はまだ変えない）。
		for ( let i = 1; i < total; i++ ) {
			tl.to(
				order[ i ],
				{
					rotation: depthProps( i - 1, total ).rotation,
					duration: TRANSITION,
					ease: 'power3.out',
					overwrite: 'auto',
				},
				0
			);
		}

		// 最前面が完全に消えてから、並び順と z-index を更新する。
		tl.add( () => {
			order.push( order.shift() );
			// 新しい深さに合わせて全カードの z-index を再設定。
			order.forEach( ( card, depth ) => {
				gsap.set( card, { zIndex: depthProps( depth, total ).zIndex } );
			} );
			// 飛んだカードを最背面へ（位置を戻して透明に）。
			gsap.set( front, {
				...depthProps( total - 1, total ),
				x: 0,
				y: 0,
				opacity: 0,
			} );
		}, EXIT_DURATION );
		tl.to(
			front,
			{
				opacity: 1,
				duration: 0.4,
				ease: 'power2.out',
				overwrite: 'auto',
			},
			EXIT_DURATION
		);
	};

	function scheduleNext() {
		clearTimeout( timer );
		timer = setTimeout( runNext, delay );
	}

	// ホバー中は停止し、離れたら再開する。
	block.addEventListener( 'mouseenter', () => clearTimeout( timer ) );
	block.addEventListener( 'mouseleave', scheduleNext );

	scheduleNext();
}

function init() {
	document.querySelectorAll( BLOCK_SELECTOR ).forEach( setupBlock );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
